import { Cookies } from 'react-cookie';
import { RowData } from '../types/transaction';
import { VoucherData } from '../types/vouchers';
import { Lounge } from 'types';
import { ILounges } from 'types/lounges';

const cookies = new Cookies();

export const getAccessToken = () => {
  return cookies.get('authToken');
};

export const getValues = (
  param: { data?: RowData | VoucherData },
  field: keyof (RowData & VoucherData),
) => {
  if (!param.data) return '-';

  if (isRowData(param.data) && field in param.data) {
    return param.data[field as keyof RowData] || '-';
  }
  if (isVoucherData(param.data) && field in param.data) {
    return param.data[field as keyof VoucherData] || '-';
  }

  return '-';
};

const isVoucherData = (data: RowData | VoucherData): data is VoucherData =>
  'voucher_id' in data;

const isRowData = (data: RowData | VoucherData): data is RowData => {
  return (data as RowData).booking_status !== undefined;
};

// Common cell renderer function
export const renderCell = (param: {
  value: string;
  data: RowData | VoucherData;
}) => (
  <div
    className={
      isRowData(param.data) && param.data.booking_status === 'Void'
        ? 'text-zinc-500'
        : ''
    }
  >
    {param.value || '-'}
  </div>
);

export const getLoungeNames = (loungeIds: string[], allLounges: Lounge[]) => {
  const names = loungeIds
    .map((id) => allLounges.find((lounge) => lounge.id === id)?.name)
    .filter(Boolean)
    .join(', ');

  return names.length > 500 ? `${names.slice(0, 500)}...` : names;
};

/**
 * Sorts lounges with the following priority:
 * 1. Already mapped lounges (alphabetically)
 * 2. Selected to be added lounges (alphabetically)
 * 3. Available lounges (alphabetically)
 */
export const sortLoungesByStatus = (
  lounges: ILounges[],
  mappedLoungeIds: string[],
  selectedLoungeIds: string[],
): ILounges[] => {
  return [...lounges].sort((loungeA, loungeB) => {
    const aIsMapped = mappedLoungeIds.includes(loungeA.id);
    const bIsMapped = mappedLoungeIds.includes(loungeB.id);
    const aIsSelected = selectedLoungeIds.includes(loungeA.id);
    const bIsSelected = selectedLoungeIds.includes(loungeB.id);

    // Priority 1: Already mapped lounges
    if (aIsMapped && !bIsMapped) return -1;
    if (!aIsMapped && bIsMapped) return 1;

    // Priority 2: Selected to be added lounges (only if not already mapped)
    if (aIsMapped && bIsMapped) {
      // Both are mapped, sort alphabetically
      return loungeA.name.localeCompare(loungeB.name);
    }
    if (!aIsMapped && !bIsMapped) {
      if (aIsSelected && !bIsSelected) return -1;
      if (!aIsSelected && bIsSelected) return 1;
      // Both are unmapped, sort alphabetically
      return loungeA.name.localeCompare(loungeB.name);
    }

    // One is mapped, one is unmapped - mapped comes first
    return 0;
  });
};
